import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Portfolio Pulse card controller.
// While Gemini is regenerating, the card collapses to a single shimmering
// row (rainbow icon + rainbow title) — no spinner, no body. When the poll
// returns text, the body reveals, the chevron + timestamp appear, and the
// header snaps from rainbow to the static gradient.
export default class extends Controller {
  static targets = ["icon", "title", "timestamp", "toggleBtn", "chevron", "body", "text"]
  static values = {
    pollUrl: String,
    language: String,
    hasText: Boolean
  }

  connect() {
    this._expanded = true
    this._viewedEventFired = false

    if (this.hasTextValue) {
      this._fireViewedEvent()
    } else if (this.pollUrlValue) {
      this._startPolling()
    }
  }

  disconnect() {
    this._stopPolling()
  }

  toggle(event) {
    event.preventDefault()
    this._expanded = !this._expanded
    this.bodyTarget.classList.toggle("hidden", !this._expanded)
    this.chevronTarget.classList.toggle("rotate-180", this._expanded)
    this.toggleBtnTarget.setAttribute("aria-expanded", this._expanded ? "true" : "false")
    if (this._expanded) this._fireViewedEvent()
  }

  // ── Polling ────────────────────────────────────────────────

  _startPolling() {
    let attempts = 0
    const maxAttempts = 20 // ~60s at 3s interval

    this._pollTimer = setInterval(async () => {
      attempts += 1
      try {
        const url = new URL(this.pollUrlValue, window.location.origin)
        if (this.languageValue) url.searchParams.set("language", this.languageValue)
        const resp = await fetch(url.toString(), {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "same-origin"
        })
        if (!resp.ok) return
        const data = await resp.json()

        if (data && data.response) {
          this._swapInText(data)
          this._stopPolling()
        }
      } catch (e) {
        // Swallow — next tick will retry.
      }

      if (attempts >= maxAttempts) {
        this._stopPolling()
        this._hideSilently()
      }
    }, 3000)
  }

  _stopPolling() {
    if (this._pollTimer) {
      clearInterval(this._pollTimer)
      this._pollTimer = null
    }
  }

  _swapInText(data) {
    this.textTarget.textContent = data.response
    this.bodyTarget.classList.remove("hidden")
    this.bodyTarget.classList.add("mt-1.5")

    this.toggleBtnTarget.classList.remove("hidden")

    if (data.updated_at) {
      this.timestampTarget.textContent = "updated just now"
      this.timestampTarget.classList.remove("hidden")
    }

    this.iconTarget.classList.remove("ai-rainbow-icon")
    this.iconTarget.classList.add("ai-gradient-icon")
    this.titleTarget.classList.remove("ai-rainbow-text")
    this.titleTarget.classList.add("ai-gradient-text")

    this._fireViewedEvent()
  }

  _hideSilently() {
    this.element.remove()
  }

  _fireViewedEvent() {
    if (this._viewedEventFired) return
    this._viewedEventFired = true
    track("Portfolio AI Summary Viewed", {
      language: this.languageValue || "en",
      cached: this.hasTextValue
    })
  }
}
